// VARIABLE_CLASSROOMS_V1
import { useState, useEffect, useRef } from 'react';
import CourseDatePicker, {
  emptyCourseDatePickerContext,
  loadCourseDatePickerContext
} from './CourseDatePicker';
// TRADITIONAL_GRADING_PATCH_20260813

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];


const WEEK_DAY_REFERENCE_TABLES = [
  { table: 'course_week_content', label: 'week descriptions' },
  { table: 'course_week_reading_assignments', label: 'readings' },
  { table: 'course_events', label: 'Events' },
  { table: 'attendance_group_weeks', label: 'attendance-group scheduling' },
  { table: 'course_week_instructors', label: 'instructor assignments' },
  { table: 'cq_question_assignments', label: 'QI assignments' }
];

function normalizedCourseDays(course) {
  const values =
    Array.isArray(course?.class_days) && course.class_days.length
      ? course.class_days
      : course?.class_day
        ? [course.class_day]
        : [];

  return Array.from(new Set(values.filter(day => DAYS.includes(day))))
    .sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
}

function taipeiToday() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());

  const byType = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return byType.year + '-' + byType.month + '-' + byType.day;
}

function utcDateValue(dateString) {
  const match = String(dateString || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return Number.NaN;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function classDateForWeek(semesterStartDate, weekNumber, day) {
  const dayIndex = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6
  };

  const match = String(semesterStartDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!match || !Number.isInteger(dayIndex[day])) {
    throw new Error('The semester start date or class day is invalid.');
  }

  const date = new Date(Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  ));

  const offset = (dayIndex[day] - date.getUTCDay() + 7) % 7;

  date.setUTCDate(
    date.getUTCDate() +
    offset +
    (Number(weekNumber) - 1) * 7
  );

  return date.toISOString().slice(0, 10);
}

async function fetchSemesterForClassDayEdit(semesterId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/semesters?id=eq.${semesterId}&select=id,start_date,week_count&limit=1`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Could not load the semester schedule (${response.status}): ${detail}`);
  }

  const rows = await response.json();
  const semester = rows?.[0] || null;

  if (
    !semester?.start_date ||
    !Number.isInteger(Number(semester.week_count)) ||
    Number(semester.week_count) < 1
  ) {
    throw new Error('The course semester does not have a valid start date and week count.');
  }

  return semester;
}

async function fetchWeeksForClassDayEdit(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*&order=week_number.asc,class_date.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Could not load course weeks (${response.status}): ${detail}`);
  }

  return response.json();
}

async function patchCourseWeekDate(weekId, classDate) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?id=eq.${weekId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ class_date: classDate })
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Could not move a teaching week (${response.status}): ${detail}`);
  }
}

async function createClassDayWeeks(rows) {
  if (!rows.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(rows)
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Could not add teaching weeks (${response.status}): ${detail}`);
  }

  return response.json();
}

async function deleteClassDayWeeks(weekIds) {
  if (!weekIds.length) return;

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?id=in.(${weekIds.join(',')})`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Could not remove unused teaching weeks (${response.status}): ${detail}`);
  }
}

async function referencedWeekLabels(weekIds) {
  if (!weekIds.length) return [];

  const idFilter = weekIds.join(',');

  const results = await Promise.all(
    WEEK_DAY_REFERENCE_TABLES.map(async reference => {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/${reference.table}?course_week_id=in.(${idFilter})&select=course_week_id&limit=1`,
        {
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': 'Bearer ' + SUPABASE_KEY
          }
        }
      );

      if (!response.ok) {
        const detail = await response.text();
        throw new Error(
          `Could not check ${reference.label} before changing class days (${response.status}): ${detail}`
        );
      }

      const rows = await response.json();
      return Array.isArray(rows) && rows.length ? reference.label : null;
    })
  );

  return results.filter(Boolean);
}

function assignRowsToDays(rows, days, semesterStartDate, weekNumber) {
  const unused = [...rows];
  const result = new Map();

  days.forEach(day => {
    if (!unused.length) return;

    const expectedDate = classDateForWeek(
      semesterStartDate,
      weekNumber,
      day
    );

    let index = unused.findIndex(row => row.class_date === expectedDate);

    if (index < 0) {
      const expectedValue = utcDateValue(expectedDate);
      let bestDistance = Number.POSITIVE_INFINITY;

      unused.forEach((row, rowIndex) => {
        const distance = Math.abs(
          utcDateValue(row.class_date) - expectedValue
        );

        if (Number.isFinite(distance) && distance < bestDistance) {
          bestDistance = distance;
          index = rowIndex;
        }
      });
    }

    if (index < 0) index = 0;

    const [row] = unused.splice(index, 1);
    result.set(day, row);
  });

  return result;
}

function remapDaySettings(settings, oldDays, newDays, replacementMap) {
  const source =
    settings && typeof settings === 'object'
      ? settings
      : {};

  const next = { ...source };

  oldDays.forEach(oldDay => {
    const targetDay =
      newDays.includes(oldDay)
        ? oldDay
        : replacementMap.get(oldDay);

    if (
      targetDay &&
      Object.prototype.hasOwnProperty.call(source, oldDay)
    ) {
      next[targetDay] = source[oldDay];
    }
  });

  newDays.forEach(day => {
    if (
      !Object.prototype.hasOwnProperty.call(next, day) &&
      Object.prototype.hasOwnProperty.call(source, day)
    ) {
      next[day] = source[day];
    }
  });

  return next;
}

async function updateCourse(id, data) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    15000
  );

  try {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/courses?id=eq.${id}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': 'Bearer ' + SUPABASE_KEY,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify(data),
        signal: controller.signal
      }
    );

    if (!response.ok) {
      const detail = await response.text();
      throw new Error(
        `Failed to update course (${response.status}): ${detail}`
      );
    }

    return true;
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error(
        'Saving the course timed out. The server did not respond.'
      );
    }

    throw err;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read the selected image.'));
    };

    image.src = objectUrl;
  });
}

function resizeImageToDataUrl(image, maxDimension = 2200, quality = 0.82) {
  let width = image.naturalWidth;
  let height = image.naturalHeight;

  if (!width || !height) {
    throw new Error('The selected image has invalid dimensions.');
  }

  if (width > maxDimension || height > maxDimension) {
    if (width >= height) {
      height = Math.round(height * (maxDimension / width));
      width = maxDimension;
    } else {
      width = Math.round(width * (maxDimension / height));
      height = maxDimension;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('This browser could not process the image.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  return canvas.toDataURL('image/jpeg', quality);
}

async function fetchCourseImageId(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}&select=course_image_drive_file_id&limit=1`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Could not refresh the course image (${response.status}): ${detail}`
    );
  }

  const rows = await response.json();
  return rows[0]?.course_image_drive_file_id || null;
}

function wait(milliseconds) {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

async function fetchInstructorOptions() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?select=id,name,email,is_superadmin&order=name.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Loading instructors failed (${response.status})`);
  }

  return response.json();
}

async function fetchCourseClassrooms(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}&select=classrooms&limit=1`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Could not load classrooms (${response.status}): ${detail}`
    );
  }

  const rows = await response.json();

  return rows[0]?.classrooms || {};
}

async function fetchCourseAccessRows(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_access?course_id=eq.${courseId}&select=instructor_id`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Loading course access failed (${response.status})`);
  }

  return response.json();
}

async function removeCourseAccess(courseId, instructorId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_access?course_id=eq.${courseId}&instructor_id=eq.${instructorId}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Removing secondary instructor failed (${response.status})`);
  }
}

async function addCourseAccess(courseId, instructorId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_access`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates'
      },
      body: JSON.stringify({
        course_id: courseId,
        instructor_id: instructorId
      })
    }
  );

  if (!response.ok) {
    throw new Error(`Adding secondary instructor failed (${response.status})`);
  }
}

async function clearInstructorAssignments(
  courseId,
  instructorId
) {
  const weeksResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=id`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!weeksResponse.ok) {
    throw new Error(
      `Loading course weeks failed (${weeksResponse.status})`
    );
  }

  const weekRows =
    await weeksResponse.json();

  const weekIds =
    (Array.isArray(weekRows)
      ? weekRows
      : [])
      .map(row => row.id)
      .filter(Boolean);

  const requests = [
    fetch(
      `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&assigned_instructor_id=eq.${instructorId}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization':
            'Bearer ' + SUPABASE_KEY,
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          assigned_instructor_id: null
        })
      }
    ),

    fetch(
      `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&assigned_instructor_id=eq.${instructorId}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization':
            'Bearer ' + SUPABASE_KEY,
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          assigned_instructor_id: null
        })
      }
    ),

    fetch(
      `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&pd_assigned_instructor_id=eq.${instructorId}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization':
            'Bearer ' + SUPABASE_KEY,
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          pd_assigned_instructor_id:
            null
        })
      }
    ),

    fetch(
      `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&teaching_instructor_id=eq.${instructorId}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization':
            'Bearer ' + SUPABASE_KEY,
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          teaching_instructor_id: null
        })
      }
    )
  ];

  if (weekIds.length) {
    requests.push(
      fetch(
        `${SUPABASE_URL}/rest/v1/course_week_instructors?course_week_id=in.(${weekIds.join(
          ','
        )})&instructor_id=eq.${instructorId}`,
        {
          method: 'DELETE',
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization':
              'Bearer ' + SUPABASE_KEY
          }
        }
      )
    );
  }

  const responses =
    await Promise.all(requests);

  const failed =
    responses.find(
      response => !response.ok
    );

  if (failed) {
    const detail =
      await failed.text();

    throw new Error(
      `Clearing instructor responsibilities failed (${failed.status}): ${
        detail || failed.statusText
      }`
    );
  }
}

export default function CourseBasicSettings({
  course,
  session,
  onCourseUpdated,
  onInstructorAccessChanged
}) {
  const canManageCourse =
    session?.isSuperadmin === true ||
    course.instructor_id ===
      session?.instructorId;

  const [classDays, setClassDays] = useState(
    normalizedCourseDays(course)
  );
  const [classDaysSaving, setClassDaysSaving] = useState(false);
  const [classTimes, setClassTimes] = useState(course.class_times || {});
  const [pdDueDay, setPdDueDay] = useState(course.pd_due_day || 'Sunday');
  const [cqDueHoursBeforeClass, setCqDueHoursBeforeClass] = useState(
    String(course.cq_due_hours_before_class ?? 1)
  );
  const [clarEnabled, setClarEnabled] = useState(
    course.cq_enabled !== false &&
    course.cq_clarificatory_enabled === true
  );
  const [clarAllowed, setClarAllowed] = useState(
    course.cq_clarificatory_allowed || ''
  );
  const [qiEnabled, setQiEnabled] = useState(
    course.cq_enabled !== false &&
    course.qi_enabled === true
  );
  const [streamsEnabled, setStreamsEnabled] = useState(
    course.streams_enabled === true
  );
  const [qiGroupsPerStream, setQiGroupsPerStream] = useState(
    course.qi_groups_per_stream || ''
  );
  const [midEvalOpen, setMidEvalOpen] = useState(
    course.mid_eval_open_date || ''
  );
  const [midEvalClose, setMidEvalClose] = useState(
    course.mid_eval_close_date || ''
  );
  const [finalEvalOpen, setFinalEvalOpen] = useState(
    course.final_eval_open_date || ''
  );
  const [finalEvalClose, setFinalEvalClose] = useState(
    course.final_eval_close_date || ''
  );
  const [isLive, setIsLive] = useState(course.is_live || false);
  const [isHonors, setIsHonors] = useState(course.is_honors === true);
  const [gradingMode, setGradingMode] = useState(
    course.grading_mode || 'ungrading'
  );

  const [
    instructorGradingMethod,
    setInstructorGradingMethod
  ] = useState(
    course.instructor_grading_method ||
    'single'
  );
  const [pdEnabled, setPdEnabled] = useState(course.pd_enabled !== false);
  const [cqEnabled, setCqEnabled] = useState(course.cq_enabled !== false);
  const [votingEnabled, setVotingEnabled] = useState(
    course.cq_enabled !== false || course.voting_enabled === true
  );
  const [attendanceEnabled, setAttendanceEnabled] = useState(
    course.attendance_enabled !== false
  );
  const [courseProgressEnabled, setCourseProgressEnabled] = useState(
    course.course_progress_enabled !== false
  );
  const [chatEnabled, setChatEnabled] = useState(
    course.chat_enabled !== false
  );
  const [selfEvalEnabled, setSelfEvalEnabled] = useState(
    course.grading_mode === 'traditional'
      ? false
      : course.self_eval_enabled !== false
  );

  const [instructorOptions, setInstructorOptions] = useState([]);
  const [additionalInstructorIds, setAdditionalInstructorIds] = useState(['']);
  const [instructorLoading, setInstructorLoading] = useState(true);
  const [instructorSaving, setInstructorSaving] = useState(false);
  const [instructorError, setInstructorError] = useState('');
  const [classrooms, setClassrooms] = useState(course.classrooms || {});

  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState('');
  const [visibilitySaving, setVisibilitySaving] = useState(false);

  const [
    datePickerContext,
    setDatePickerContext
  ] = useState(
    emptyCourseDatePickerContext()
  );

  const activeSavesRef = useRef(0);
  const statusTimerRef = useRef(null);
  const fieldTimersRef = useRef({});
  const courseSnapshotRef = useRef(course);
  const onCourseUpdatedRef = useRef(onCourseUpdated);
  const courseImageInputRef = useRef(null);

  const [courseImageId, setCourseImageId] = useState(
    course.course_image_drive_file_id || null
  );
  const [courseImageStatus, setCourseImageStatus] = useState('');
  const [courseImageUploading, setCourseImageUploading] = useState(false);
  const [courseImageRemoving, setCourseImageRemoving] = useState(false);

  useEffect(() => {
    courseSnapshotRef.current = course;
  }, [course]);

  useEffect(() => {
    onCourseUpdatedRef.current = onCourseUpdated;
  }, [onCourseUpdated]);

  useEffect(() => {
    Object.values(fieldTimersRef.current).forEach(timerId => {
      window.clearTimeout(timerId);
    });
    fieldTimersRef.current = {};

    setCourseImageId(course.course_image_drive_file_id || null);
    setCourseImageStatus('');
    setCourseImageUploading(false);
    setCourseImageRemoving(false);
    setClassrooms(course.classrooms || {});
    setClassDays(normalizedCourseDays(course));
    setClassDaysSaving(false);
    setClassTimes(course.class_times || {});
    setPdDueDay(course.pd_due_day || 'Sunday');
    setCqDueHoursBeforeClass(
      String(course.cq_due_hours_before_class ?? 1)
    );
    setClarEnabled(
      course.cq_enabled !== false &&
      course.cq_clarificatory_enabled === true
    );
    setClarAllowed(course.cq_clarificatory_allowed || '');
    setQiEnabled(
      course.cq_enabled !== false &&
      course.qi_enabled === true
    );
    setStreamsEnabled(course.streams_enabled === true);
    setQiGroupsPerStream(course.qi_groups_per_stream || '');
    setMidEvalOpen(course.mid_eval_open_date || '');
    setMidEvalClose(course.mid_eval_close_date || '');
    setFinalEvalOpen(course.final_eval_open_date || '');
    setFinalEvalClose(course.final_eval_close_date || '');
    setIsLive(course.is_live || false);
    setIsHonors(course.is_honors === true);
    setGradingMode(course.grading_mode || 'ungrading');

    setInstructorGradingMethod(
      course.instructor_grading_method ||
      'single'
    );

    setPdEnabled(course.pd_enabled !== false);
    setCqEnabled(course.cq_enabled !== false);
    setVotingEnabled(
      course.cq_enabled !== false ||
      course.voting_enabled === true
    );
    setAttendanceEnabled(course.attendance_enabled !== false);
    setCourseProgressEnabled(
      course.course_progress_enabled !== false
    );
    setChatEnabled(
      course.chat_enabled !== false
    );
    setSelfEvalEnabled(
      course.grading_mode === 'traditional'
        ? false
        : course.self_eval_enabled !== false
    );

    if (courseImageInputRef.current) {
      courseImageInputRef.current.value = '';
    }

    setSaveStatus('');
    setError('');

    // Intentionally reset when a different course is selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.id]);

  useEffect(() => {
    let cancelled = false;

    setDatePickerContext(
      emptyCourseDatePickerContext()
    );

    loadCourseDatePickerContext({
      id:
        course.id,

      instructor_id:
        course.instructor_id
    })
      .then(
        context => {
          if (!cancelled) {
            setDatePickerContext(
              context
            );
          }
        }
      )
      .catch(
        err => {
          if (!cancelled) {
            console.warn(
              'Course calendar context failed:',
              err
            );
          }
        }
      );

    return () => {
      cancelled = true;
    };
  }, [
    course.id,
    course.instructor_id
  ]);


  useEffect(() => {
    let cancelled = false;

    fetchCourseClassrooms(course.id)
      .then(nextClassrooms => {
        if (cancelled) return;

        setClassrooms(nextClassrooms);

        const updatedCourse = {
          ...courseSnapshotRef.current,
          classrooms: nextClassrooms
        };

        courseSnapshotRef.current = updatedCourse;

        if (onCourseUpdatedRef.current) {
          onCourseUpdatedRef.current(updatedCourse);
        }
      })
      .catch(err => {
        if (cancelled) return;

        console.error(
          'Classroom refresh failed:',
          err
        );

        setError(
          err?.message ||
            'Could not load classroom settings.'
        );
      });

    return () => {
      cancelled = true;
    };
  }, [course.id]);

  useEffect(() => {
    let cancelled = false;

    if (!canManageCourse) {
      setInstructorOptions([]);
      setAdditionalInstructorIds(
        ['']
      );
      setInstructorLoading(false);
      setInstructorError('');
      return undefined;
    }

    setInstructorLoading(true);
    setInstructorError('');

    Promise.all([
      fetchInstructorOptions(),
      fetchCourseAccessRows(course.id)
    ])
      .then(([options, accessRows]) => {
        if (cancelled) return;

        const cleanOptions = Array.isArray(options)
          ? options
          : [];

        const optionById = new Map(
          cleanOptions.map(instructor => [
            instructor.id,
            instructor
          ])
        );

        const additionalIds = Array.from(
          new Set(
            (Array.isArray(accessRows) ? accessRows : [])
              .map(row => row.instructor_id)
              .filter(
                id =>
                  id &&
                  id !== course.instructor_id
              )
          )
        ).sort((a, b) => {
          const instructorA = optionById.get(a);
          const instructorB = optionById.get(b);

          return String(
            instructorA?.name ||
              instructorA?.email ||
              ''
          ).localeCompare(
            String(
              instructorB?.name ||
                instructorB?.email ||
                ''
            )
          );
        });

        const slots = additionalIds.length
          ? [...additionalIds]
          : [''];

        if (
          slots.length > 1 &&
          (slots.length - 1) % 2 !== 0
        ) {
          slots.push('');
        }

        setInstructorOptions(cleanOptions);
        setAdditionalInstructorIds(slots);
      })
      .catch(err => {
        if (cancelled) return;

        console.error(
          'Instructor settings load failed:',
          err
        );

        setInstructorError(
          err?.message ||
            'Could not load instructor settings.'
        );
      })
      .finally(() => {
        if (!cancelled) {
          setInstructorLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    course.id,
    course.instructor_id,
    canManageCourse
  ]);

  async function handleAdditionalInstructorChange(
    slotIndex,
    nextInstructorId
  ) {
    if (instructorSaving) return;

    const currentInstructorId =
      additionalInstructorIds[slotIndex] || '';

    if (currentInstructorId === nextInstructorId) {
      return;
    }

    if (
      nextInstructorId &&
      additionalInstructorIds.some(
        (id, index) =>
          index !== slotIndex &&
          id === nextInstructorId
      )
    ) {
      setInstructorError(
        'That instructor is already assigned to this course.'
      );
      return;
    }

    setInstructorSaving(true);
    setInstructorError('');

    try {
      if (nextInstructorId) {
        await addCourseAccess(
          course.id,
          nextInstructorId
        );
      }

      if (currentInstructorId) {
        await clearInstructorAssignments(
          course.id,
          currentInstructorId
        );

        await removeCourseAccess(
          course.id,
          currentInstructorId
        );
      }

      setAdditionalInstructorIds(prev =>
        prev.map((id, index) =>
          index === slotIndex
            ? nextInstructorId
            : id
        )
      );

      if (onInstructorAccessChanged) {
        onInstructorAccessChanged();
      }
    } catch (err) {
      console.error(
        'Additional instructor save failed:',
        err
      );

      setInstructorError(
        err?.message ||
          'Could not save the instructor.'
      );
    } finally {
      setInstructorSaving(false);
    }
  }

  function addInstructorRow() {
    if (
      additionalInstructorIds.some(id => !id)
    ) {
      return;
    }

    setAdditionalInstructorIds(prev => [
      ...prev,
      '',
      ''
    ]);
  }

  useEffect(() => {
    return () => {
      Object.values(fieldTimersRef.current).forEach(timerId => {
        window.clearTimeout(timerId);
      });

      if (statusTimerRef.current) {
        window.clearTimeout(statusTimerRef.current);
      }
    };
  }, []);

  function beginSave() {
    activeSavesRef.current += 1;

    if (statusTimerRef.current) {
      window.clearTimeout(statusTimerRef.current);
      statusTimerRef.current = null;
    }

    setSaveStatus('Saving...');
  }

  function finishSave() {
    activeSavesRef.current = Math.max(
      0,
      activeSavesRef.current - 1
    );

    if (activeSavesRef.current === 0) {
      setSaveStatus('Saved');

      statusTimerRef.current = window.setTimeout(() => {
        setSaveStatus('');
        statusTimerRef.current = null;
      }, 1500);
    }
  }

  async function saveChanges(changes) {
    beginSave();
    setError('');

    try {
      await updateCourse(course.id, changes);

      const updatedCourse = {
        ...courseSnapshotRef.current,
        ...changes
      };

      courseSnapshotRef.current = updatedCourse;

      if (onCourseUpdated) {
        onCourseUpdated(updatedCourse);
      }

      return true;
    } catch (err) {
      console.error(
        'Course setting autosave failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save course settings.'
      );

      return false;
    } finally {
      finishSave();
    }
  }

  function scheduleSave(key, changes, delay = 500) {
    if (fieldTimersRef.current[key]) {
      window.clearTimeout(
        fieldTimersRef.current[key]
      );
    }

    fieldTimersRef.current[key] =
      window.setTimeout(() => {
        delete fieldTimersRef.current[key];
        saveChanges(changes);
      }, delay);
  }

  function flushScheduledSave(key, changes) {
    if (!fieldTimersRef.current[key]) {
      return;
    }

    window.clearTimeout(
      fieldTimersRef.current[key]
    );

    delete fieldTimersRef.current[key];
    saveChanges(changes);
  }

  function handleFeatureToggle(field, setter, checked) {
    setter(checked);
    saveChanges({ [field]: checked });
  }

  function handleCqEnabledChange(checked) {
    setCqEnabled(checked);

    if (!checked) {
      setClarEnabled(false);
      setQiEnabled(false);

      saveChanges({
        cq_enabled: false,
        cq_clarificatory_enabled: false,
        qi_enabled: false
      });

      return;
    }

    setVotingEnabled(true);

    saveChanges({
      cq_enabled: true,
      voting_enabled: true
    });
  }

  function handleVotingEnabledChange(checked) {
    if (cqEnabled) {
      setVotingEnabled(true);
      return;
    }

    setVotingEnabled(checked);
    saveChanges({ voting_enabled: checked });
  }

  function handleAttendanceEnabledChange(checked) {
    setAttendanceEnabled(checked);

    saveChanges(
      checked
        ? { attendance_enabled: true }
        : {
            attendance_enabled: false,
            group_based_attendance: false
          }
    );
  }

  function handleClassTimeChange(day, value) {
    const nextClassTimes = {
      ...classTimes,
      [day]: value
    };

    setClassTimes(nextClassTimes);

    saveChanges({
      class_times: nextClassTimes
    });
  }


  function handleClassDayToggle(day, checked) {
    setClassDays(previous => {
      const next =
        checked
          ? [...previous, day]
          : previous.filter(value => value !== day);

      return Array.from(new Set(next))
        .sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
    });
  }

  async function handleSaveClassDays() {
    if (classDaysSaving) return;

    const oldDays = normalizedCourseDays(courseSnapshotRef.current);

    const newDays = Array.from(
      new Set(classDays.filter(day => DAYS.includes(day)))
    ).sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));

    if (!newDays.length) {
      setError('Select at least one class day.');
      return;
    }

    if (
      oldDays.length === newDays.length &&
      oldDays.every((day, index) => day === newDays[index])
    ) {
      return;
    }

    if (!course.semester_id) {
      setError('This course does not have a semester.');
      return;
    }

    setClassDaysSaving(true);
    setError('');
    beginSave();

    const changedWeeks = [];
    let createdWeekIds = [];
    let courseWasUpdated = false;

    const previousClassTimes = { ...classTimes };
    const previousClassrooms = { ...classrooms };

    const previousCourseSettings = {
      class_days: oldDays,
      class_day: oldDays.length === 1 ? oldDays[0] : null,
      class_times: previousClassTimes,
      class_time:
        oldDays.length === 1
          ? (
              previousClassTimes[oldDays[0]] ||
              courseSnapshotRef.current?.class_time ||
              null
            )
          : null,
      classrooms: previousClassrooms
    };

    try {
      const [semester, weeks] = await Promise.all([
        fetchSemesterForClassDayEdit(course.semester_id),
        fetchWeeksForClassDayEdit(course.id)
      ]);

      if (!Array.isArray(weeks) || !weeks.length) {
        throw new Error('This course has no teaching weeks to move.');
      }

      const today = taipeiToday();

      const removedDays = oldDays.filter(day => !newDays.includes(day));
      const addedDays = newDays.filter(day => !oldDays.includes(day));

      const replacementMap = new Map();
      const replacementCount = Math.min(
        removedDays.length,
        addedDays.length
      );

      for (let index = 0; index < replacementCount; index += 1) {
        replacementMap.set(removedDays[index], addedDays[index]);
      }

      const removedWithoutReplacement =
        removedDays.slice(replacementCount);

      const addedWithoutSource =
        addedDays.slice(replacementCount);

      const weeksByNumber = new Map();

      weeks.forEach(week => {
        const number = Number(week.week_number);

        if (!weeksByNumber.has(number)) {
          weeksByNumber.set(number, []);
        }

        weeksByNumber.get(number).push(week);
      });

      const updates = [];
      const additions = [];
      const removals = [];

      for (
        let weekNumber = 1;
        weekNumber <= Number(semester.week_count);
        weekNumber += 1
      ) {
        const weekRows = (weeksByNumber.get(weekNumber) || [])
          .slice()
          .sort((a, b) =>
            String(a.class_date || '').localeCompare(
              String(b.class_date || '')
            )
          );

        if (!weekRows.length) continue;

        const activeOldDays = oldDays.filter(day =>
          classDateForWeek(
            semester.start_date,
            weekNumber,
            day
          ) > today
        );

        const futureRows = weekRows.filter(
          row => String(row.class_date || '') > today
        );

        if (futureRows.length < activeOldDays.length) {
          throw new Error(
            `Week ${weekNumber} has an irregular future schedule, so its class days cannot be changed safely.`
          );
        }

        const assignedRows = assignRowsToDays(
          futureRows,
          activeOldDays,
          semester.start_date,
          weekNumber
        );

        activeOldDays.forEach(oldDay => {
          const row = assignedRows.get(oldDay);
          if (!row) return;

          const targetDay =
            newDays.includes(oldDay)
              ? oldDay
              : replacementMap.get(oldDay) || null;

          if (!targetDay) {
            if (removedWithoutReplacement.includes(oldDay)) {
              removals.push(row);
            }
            return;
          }

          const targetDate = classDateForWeek(
            semester.start_date,
            weekNumber,
            targetDay
          );

          if (targetDate > today && row.class_date !== targetDate) {
            updates.push({
              id: row.id,
              before: row.class_date,
              after: targetDate
            });
          }
        });

        addedWithoutSource.forEach(day => {
          const targetDate = classDateForWeek(
            semester.start_date,
            weekNumber,
            day
          );

          if (targetDate <= today) return;

          if (weekRows.some(row => row.class_date === targetDate)) {
            return;
          }

          const base = futureRows[0] || weekRows[0];

          const newWeek = {
            course_id: course.id,
            week_number: weekNumber,
            class_date: targetDate,
            cq_enabled:
              base?.cq_enabled ?? (course.cq_enabled !== false),
            attendance_enabled:
              base?.attendance_enabled ??
              (course.attendance_enabled !== false),
            pd_enabled: false
          };

          if (
            Object.prototype.hasOwnProperty.call(
              base || {},
              'teaching_instructor_id'
            )
          ) {
            newWeek.teaching_instructor_id =
              base.teaching_instructor_id || null;
          }

          additions.push(newWeek);
        });
      }

      const removalIds = removals
        .map(row => row.id)
        .filter(Boolean);

      if (removalIds.length) {
        const labels = await referencedWeekLabels(removalIds);

        if (labels.length) {
          throw new Error(
            'The removed class day still has future ' +
            labels.join(', ') +
            '. Move or remove those items first, then change the class days.'
          );
        }
      }

      for (const update of updates) {
        await patchCourseWeekDate(update.id, update.after);
        changedWeeks.push(update);
      }

      if (additions.length) {
        const created = await createClassDayWeeks(additions);

        createdWeekIds = (
          Array.isArray(created)
            ? created
            : []
        )
          .map(row => row.id)
          .filter(Boolean);
      }

      const sourceTimes = { ...classTimes };

      if (
        oldDays.length === 1 &&
        !sourceTimes[oldDays[0]] &&
        courseSnapshotRef.current?.class_time
      ) {
        sourceTimes[oldDays[0]] =
          String(courseSnapshotRef.current.class_time).slice(0, 5);
      }

      const nextClassTimes = remapDaySettings(
        sourceTimes,
        oldDays,
        newDays,
        replacementMap
      );

      const nextClassrooms = remapDaySettings(
        classrooms,
        oldDays,
        newDays,
        replacementMap
      );

      const courseChanges = {
        class_days: newDays,
        class_day: newDays.length === 1 ? newDays[0] : null,
        class_times: nextClassTimes,
        class_time:
          newDays.length === 1
            ? (nextClassTimes[newDays[0]] || null)
            : null,
        classrooms: nextClassrooms
      };

      await updateCourse(course.id, courseChanges);
      courseWasUpdated = true;

      if (removalIds.length) {
        await deleteClassDayWeeks(removalIds);
      }

      setClassDays(newDays);
      setClassTimes(nextClassTimes);
      setClassrooms(nextClassrooms);

      const updatedCourse = {
        ...courseSnapshotRef.current,
        ...courseChanges
      };

      courseSnapshotRef.current = updatedCourse;

      if (onCourseUpdated) {
        onCourseUpdated(updatedCourse);
      }
    } catch (err) {
      console.error('Class day update failed:', err);

      try {
        if (courseWasUpdated) {
          await updateCourse(course.id, previousCourseSettings);
        }

        if (createdWeekIds.length) {
          await deleteClassDayWeeks(createdWeekIds);
        }

        for (const update of [...changedWeeks].reverse()) {
          await patchCourseWeekDate(update.id, update.before);
        }
      } catch (rollbackError) {
        console.error('Class day rollback failed:', rollbackError);
      }

      setClassDays(oldDays);
      setClassTimes(previousClassTimes);
      setClassrooms(previousClassrooms);

      setError(
        err?.message ||
        'Failed to change the class days.'
      );
    } finally {
      setClassDaysSaving(false);
      finishSave();
    }
  }

  function handlePdDueDayChange(value) {
    setPdDueDay(value);

    saveChanges({
      pd_due_day: value
    });
  }

  function handleCqDueHoursChange(value) {
    setCqDueHoursBeforeClass(value);

    const numericValue = Number(value);

    if (
      value === '' ||
      !Number.isInteger(numericValue) ||
      numericValue < 0
    ) {
      return;
    }

    scheduleSave(
      'cqDueHoursBeforeClass',
      {
        cq_due_hours_before_class: numericValue
      }
    );
  }

  function handleCqDueHoursBlur() {
    const numericValue = Number(cqDueHoursBeforeClass);

    if (
      cqDueHoursBeforeClass === '' ||
      !Number.isInteger(numericValue) ||
      numericValue < 0
    ) {
      setCqDueHoursBeforeClass(
        String(
          courseSnapshotRef.current
            ?.cq_due_hours_before_class ?? 1
        )
      );
      return;
    }

    flushScheduledSave(
      'cqDueHoursBeforeClass',
      {
        cq_due_hours_before_class: numericValue
      }
    );
  }

  function handleClarEnabledChange(checked) {
    setClarEnabled(checked);

    const changes = {
      cq_clarificatory_enabled: checked
    };

    if (
      checked &&
      clarAllowed &&
      Number(clarAllowed) >= 1
    ) {
      changes.cq_clarificatory_allowed =
        Number(clarAllowed);
    }

    saveChanges(changes);
  }

  function handleClarAllowedChange(value) {
    setClarAllowed(value);

    if (!value || Number(value) < 1) {
      return;
    }

    scheduleSave(
      'clarAllowed',
      {
        cq_clarificatory_allowed:
          Number(value)
      }
    );
  }

  function handleClarAllowedBlur() {
    if (!clarAllowed || Number(clarAllowed) < 1) {
      return;
    }

    flushScheduledSave(
      'clarAllowed',
      {
        cq_clarificatory_allowed:
          Number(clarAllowed)
      }
    );
  }

  function handleQiEnabledChange(checked) {
    setQiEnabled(checked);

    const changes = {
      qi_enabled: checked
    };

    if (
      checked &&
      qiGroupsPerStream &&
      Number(qiGroupsPerStream) >= 1
    ) {
      changes.qi_groups_per_stream =
        Number(qiGroupsPerStream);
    }

    saveChanges(changes);
  }

  function handleQiGroupsChange(value) {
    setQiGroupsPerStream(value);

    if (!value || Number(value) < 1) {
      return;
    }

    scheduleSave(
      'qiGroupsPerStream',
      {
        qi_groups_per_stream:
          Number(value)
      }
    );
  }

  function handleQiGroupsBlur() {
    if (
      !qiGroupsPerStream ||
      Number(qiGroupsPerStream) < 1
    ) {
      return;
    }

    flushScheduledSave(
      'qiGroupsPerStream',
      {
        qi_groups_per_stream:
          Number(qiGroupsPerStream)
      }
    );
  }

  function handleDateChange(
    setter,
    field,
    value
  ) {
    setter(value);

    saveChanges({
      [field]: value || null
    });
  }

  function handleGradingModeChange(mode) {
    setGradingMode(mode);

    if (mode === 'traditional') {
      setSelfEvalEnabled(false);

      saveChanges({
        grading_mode: mode,
        self_eval_enabled: false
      });

      return;
    }

    saveChanges({
      grading_mode: mode
    });
  }

  function handleInstructorGradingMethodChange(
    method
  ) {
    const next =
      method === 'average'
        ? 'average'
        : 'single';

    setInstructorGradingMethod(
      next
    );

    saveChanges({
      instructor_grading_method:
        next
    });
  }


  async function pollForCourseImage(expectedOldId, expectRemoval = false) {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      await wait(750);

      const freshId = await fetchCourseImageId(course.id);

      if (expectRemoval) {
        if (!freshId) return null;
      } else if (freshId && freshId !== expectedOldId) {
        return freshId;
      }
    }

    throw new Error(
      expectRemoval
        ? 'The image removal was sent, but the course record did not update.'
        : 'The image upload was sent, but the course record did not update.'
    );
  }

  function applyCourseImageId(nextId) {
    setCourseImageId(nextId || null);

    const updatedCourse = {
      ...courseSnapshotRef.current,
      course_image_drive_file_id: nextId || null
    };

    courseSnapshotRef.current = updatedCourse;

    if (onCourseUpdated) {
      onCourseUpdated(updatedCourse);
    }
  }

  async function handleCourseImageUpload(file) {
    if (!file || courseImageUploading || courseImageRemoving) return;

    if (!API_URL) {
      setError('REACT_APP_API_URL is not configured.');
      return;
    }

    if (!file.type || !file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }

    setCourseImageUploading(true);
    setCourseImageStatus('Processing image...');
    setError('');

    const previousId = courseImageId || null;

    try {
      const image = await loadImageFromFile(file);
      const dataUrl = resizeImageToDataUrl(image, 2200, 0.82);
      const base64Data = dataUrl.split(',')[1];

      if (!base64Data) {
        throw new Error('The processed image data is invalid.');
      }

      setCourseImageStatus('Uploading...');

      await fetch(API_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'uploadCourseImage',
          courseId: course.id,
          base64Data,
          mimeType: 'image/jpeg'
        })
      });

      const newId = await pollForCourseImage(previousId, false);

      applyCourseImageId(newId);
      setCourseImageStatus('Image saved.');

      window.setTimeout(() => {
        setCourseImageStatus('');
      }, 1800);
    } catch (err) {
      console.error('Course image upload failed:', err);

      setError(
        err?.message ||
        'Failed to upload the course image.'
      );

      setCourseImageStatus('');
    } finally {
      setCourseImageUploading(false);

      if (courseImageInputRef.current) {
        courseImageInputRef.current.value = '';
      }
    }
  }

  async function handleRemoveCourseImage() {
    if (!courseImageId || courseImageUploading || courseImageRemoving) {
      return;
    }

    if (!API_URL) {
      setError('REACT_APP_API_URL is not configured.');
      return;
    }

    setCourseImageRemoving(true);
    setCourseImageStatus('Removing image...');
    setError('');

    try {
      await fetch(API_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'deleteCourseImage',
          courseId: course.id
        })
      });

      await pollForCourseImage(courseImageId, true);

      applyCourseImageId(null);
      setCourseImageStatus('Image removed.');

      window.setTimeout(() => {
        setCourseImageStatus('');
      }, 1800);
    } catch (err) {
      console.error('Course image removal failed:', err);

      setError(
        err?.message ||
        'Failed to remove the course image.'
      );

      setCourseImageStatus('');
    } finally {
      setCourseImageRemoving(false);
    }
  }

  async function handleToggleLive() {
    const nextIsLive = !isLive;

    setVisibilitySaving(true);

    const saved = await saveChanges({
      is_live: nextIsLive
    });

    if (saved) {
      setIsLive(nextIsLive);
    }

    setVisibilitySaving(false);
  }

  const datePickerCourse = {
    ...course,

    self_eval_enabled:
      selfEvalEnabled,

    mid_eval_open_date:
      midEvalOpen ||
      null,

    mid_eval_close_date:
      midEvalClose ||
      null,

    final_eval_open_date:
      finalEvalOpen ||
      null,

    final_eval_close_date:
      finalEvalClose ||
      null
  };


  const primaryInstructor = instructorOptions.find(
    instructor => instructor.id === course.instructor_id
  );

  const canAddInstructorRow =
    additionalInstructorIds.every(Boolean);

  function availableInstructorsForSlot(slotIndex) {
    const currentId =
      additionalInstructorIds[slotIndex] || '';

    const usedIds = new Set(
      additionalInstructorIds.filter(
        (id, index) =>
          id && index !== slotIndex
      )
    );

    return instructorOptions.filter(
      instructor =>
        instructor.id !== course.instructor_id &&
        (
          instructor.id === currentId ||
          !usedIds.has(instructor.id)
        )
    );
  }

  function renderInstructorSelect(slotIndex) {
    return (
      <div
        key={slotIndex}
        style={styles.instructorSettingCard}
      >
        <label style={styles.featureSettingTitle}>
          Instructor {slotIndex + 2}
        </label>

        <select
          style={styles.compactInput}
          value={additionalInstructorIds[slotIndex] || ''}
          disabled={instructorLoading || instructorSaving}
          onChange={event =>
            handleAdditionalInstructorChange(
              slotIndex,
              event.target.value
            )
          }
        >
          <option value="">None</option>

          {availableInstructorsForSlot(slotIndex).map(
            instructor => (
              <option
                key={instructor.id}
                value={instructor.id}
              >
                {instructor.name}
                {instructor.email
                  ? ` — ${instructor.email}`
                  : ''}
              </option>
            )
          )}
        </select>
      </div>
    );
  }

  return (
    <div>
      <div style={styles.headingRow}>
        <h2 style={styles.subheading}>
          Basic Settings
        </h2>

        {saveStatus && (
          <span
            style={{
              ...styles.saveStatus,
              ...(saveStatus === 'Saved'
                ? styles.saveStatusSaved
                : {})
            }}
          >
            {saveStatus}
          </span>
        )}
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionHeading}>
          Class Days
        </h3>

        <p
          style={{
            ...styles.muted,
            marginBottom: '12px'
          }}
        >
          Changing a day moves future teaching
          weeks while preserving their existing
          week IDs and attached course content.
          Past teaching dates stay unchanged.
          Holidays are not skipped automatically.
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px 16px',
            marginBottom: '12px'
          }}
        >
          {DAYS.map(day => (
            <label
              key={day}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                cursor:
                  classDaysSaving
                    ? 'default'
                    : 'pointer'
              }}
            >
              <input
                type="checkbox"
                checked={classDays.includes(day)}
                disabled={classDaysSaving}
                onChange={e =>
                  handleClassDayToggle(
                    day,
                    e.target.checked
                  )
                }
              />

              {day}
            </label>
          ))}
        </div>

        <button
          type="button"
          disabled={
            classDaysSaving ||
            !classDays.length ||
            (
              normalizedCourseDays(course).length === classDays.length &&
              normalizedCourseDays(course).every(
                (day, index) => day === classDays[index]
              )
            )
          }
          onClick={handleSaveClassDays}
          style={{
            border: 'none',
            borderRadius: '8px',
            padding: '9px 14px',
            background: '#2f6f9f',
            color: 'white',
            fontSize: '13px',
            fontWeight: '700',
            cursor:
              classDaysSaving
                ? 'default'
                : 'pointer',
            opacity:
              (
                classDaysSaving ||
                !classDays.length
              )
                ? 0.55
                : 1
          }}
        >
          {classDaysSaving
            ? 'Updating class days…'
            : 'Save class days'}
        </button>
      </div>


      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      <div>
        <div style={styles.section}>
          <h3 style={styles.sectionHeading}>
            Course Image
          </h3>

          <p style={styles.sectionHelp}>
            Used as the course artwork in the student system.
            Large images are automatically resized before upload.
          </p>

          <div style={styles.courseImageRow}>
            <div style={styles.courseImagePreview}>
              {courseImageId ? (
                <img
                  key={courseImageId}
                  src={`https://lh3.googleusercontent.com/d/${courseImageId}=w1200`}
                  alt=""
                  style={styles.courseImage}
                  referrerPolicy="no-referrer"
                  onError={event => {
                    const image = event.currentTarget;

                    if (image.dataset.driveFallback === 'true') {
                      image.style.display = 'none';
                      return;
                    }

                    image.dataset.driveFallback = 'true';
                    image.src =
                      `https://drive.google.com/thumbnail?id=${courseImageId}&sz=w1200`;
                  }}
                />
              ) : (
                <div style={styles.courseImagePlaceholder}>
                  No course image
                </div>
              )}
            </div>

            <div style={styles.courseImageActions}>
              <input
                ref={courseImageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={styles.hiddenFileInput}
                onChange={e => {
                  const file = e.target.files?.[0] || null;

                  if (file) {
                    handleCourseImageUpload(file);
                  }
                }}
              />

              <button
                type="button"
                style={styles.imageUploadBtn}
                onClick={() =>
                  courseImageInputRef.current?.click()
                }
                disabled={
                  courseImageUploading ||
                  courseImageRemoving
                }
              >
                {courseImageUploading
                  ? 'Uploading...'
                  : courseImageId
                    ? 'Replace Image'
                    : 'Choose Image'}
              </button>

              {courseImageId && (
                <button
                  type="button"
                  style={styles.imageRemoveBtn}
                  onClick={handleRemoveCourseImage}
                  disabled={
                    courseImageUploading ||
                    courseImageRemoving
                  }
                >
                  {courseImageRemoving
                    ? 'Removing...'
                    : 'Remove Image'}
                </button>
              )}

              {courseImageStatus && (
                <div style={styles.imageStatus}>
                  {courseImageStatus}
                </div>
              )}
            </div>
          </div>
        </div>

        {canManageCourse && (
        <div style={styles.section}>
          <h3 style={styles.sectionHeading}>
            Instructors
          </h3>

          <p style={styles.sectionHelp}>
            The primary instructor owns and manages the course.
            Additional instructors can access and grade the course,
            but cannot change its settings.
          </p>

          <div style={styles.instructorTopRow}>
            <div style={styles.instructorSettingCard}>
              <label style={styles.featureSettingTitle}>
                Primary Instructor
              </label>

              <div style={styles.primaryInstructorName}>
                {primaryInstructor?.name || 'Primary instructor'}
              </div>

              {primaryInstructor?.email && (
                <div style={styles.primaryInstructorEmail}>
                  {primaryInstructor.email}
                </div>
              )}
            </div>

            {renderInstructorSelect(0)}

            <button
              type="button"
              style={{
                ...styles.addInstructorButton,
                ...(!canAddInstructorRow
                  ? styles.addInstructorButtonDisabled
                  : {})
              }}
              disabled={
                instructorLoading ||
                instructorSaving ||
                !canAddInstructorRow
              }
              onClick={addInstructorRow}
              title={
                canAddInstructorRow
                  ? 'Add two more instructors'
                  : 'Fill the existing instructor fields first'
              }
            >
              +
            </button>
          </div>

          {additionalInstructorIds.length > 1 && (
            <div style={styles.additionalInstructorRows}>
              {Array.from({
                length: Math.ceil(
                  (additionalInstructorIds.length - 1) / 2
                )
              }).map((_, rowIndex) => {
                const firstIndex =
                  1 + rowIndex * 2;

                const secondIndex =
                  firstIndex + 1;

                return (
                  <div
                    key={firstIndex}
                    style={styles.additionalInstructorRow}
                  >
                    {renderInstructorSelect(firstIndex)}

                    {secondIndex <
                      additionalInstructorIds.length &&
                      renderInstructorSelect(secondIndex)}
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.instructorSettingNote}>
            {instructorSaving
              ? 'Saving...'
              : canAddInstructorRow
                ? 'Use + to add another pair of instructor fields.'
                : 'Fill the existing instructor fields before adding another row.'}
          </div>

          {instructorError && (
            <div
              style={{
                ...styles.error,
                marginTop: '10px'
              }}
            >
              {instructorError}
            </div>
          )}
        </div>
        )}

        <div style={styles.section}>
          <h3 style={styles.sectionHeading}>
            Feature Toggles
          </h3>

          <div style={styles.featureRows}>
            <div style={styles.featureRow}>
              <div style={styles.featurePrimary}>
                <label style={styles.featurePrimaryLabel}>
                  <input
                    type="checkbox"
                    checked={pdEnabled}
                    onChange={e =>
                      handleFeatureToggle(
                        'pd_enabled',
                        setPdEnabled,
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Philosophical Diary
                  </span>
                </label>
              </div>

              <div
                style={{
                  ...styles.featureSettingPanel,
                  opacity: pdEnabled ? 1 : 0.42
                }}
              >
                <label style={styles.featureSettingTitle}>
                  Due day
                </label>

                <select
                  style={styles.compactInput}
                  value={pdDueDay}
                  disabled={!pdEnabled}
                  onChange={e =>
                    handlePdDueDayChange(
                      e.target.value
                    )
                  }
                >
                  {DAYS.map(day => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.featureEmptyCell} />
            </div>

            <div style={styles.featureRow}>
              <div style={styles.featurePrimary}>
                <label style={styles.featurePrimaryLabel}>
                  <input
                    type="checkbox"
                    checked={cqEnabled}
                    onChange={e =>
                      handleCqEnabledChange(
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Critical Questions
                  </span>
                </label>
              </div>

              <div
                style={{
                  ...styles.featureSettingPanel,
                  opacity: cqEnabled ? 1 : 0.42
                }}
              >
                <label style={styles.featureSettingTitle}>
                  Questions due
                </label>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}
                >
                  <input
                    style={{
                      ...styles.compactInput,
                      width: '82px'
                    }}
                    type="number"
                    min="0"
                    step="1"
                    value={cqDueHoursBeforeClass}
                    disabled={!cqEnabled}
                    onChange={e =>
                      handleCqDueHoursChange(
                        e.target.value
                      )
                    }
                    onBlur={handleCqDueHoursBlur}
                  />

                  <span style={styles.featureSettingTitle}>
                    hours before class
                  </span>
                </div>

                <label
                  style={{
                    ...styles.featureSecondaryToggle,
                    cursor: cqEnabled
                      ? 'pointer'
                      : 'default'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={clarEnabled}
                    disabled={!cqEnabled}
                    onChange={e =>
                      handleClarEnabledChange(
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Clarificatory Questions
                  </span>
                </label>

                <label
                  style={{
                    ...styles.featureSettingTitle,
                    opacity:
                      cqEnabled && clarEnabled
                        ? 1
                        : 0.6
                  }}
                >
                  How many allowed per student
                </label>

                <input
                  style={{
                    ...styles.compactInput,
                    opacity:
                      cqEnabled && clarEnabled
                        ? 1
                        : 0.65
                  }}
                  type="number"
                  min="1"
                  value={clarAllowed}
                  disabled={
                    !cqEnabled ||
                    !clarEnabled
                  }
                  onChange={e =>
                    handleClarAllowedChange(
                      e.target.value
                    )
                  }
                  onBlur={handleClarAllowedBlur}
                  placeholder="e.g. 3"
                />
              </div>

              <div
                style={{
                  ...styles.featureSettingPanel,
                  opacity: cqEnabled ? 1 : 0.42
                }}
              >
                <label
                  style={{
                    ...styles.featureSecondaryToggle,
                    cursor: cqEnabled
                      ? 'pointer'
                      : 'default'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={qiEnabled}
                    disabled={!cqEnabled}
                    onChange={e =>
                      handleQiEnabledChange(
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Question Improvement
                  </span>
                </label>

                <label
                  style={{
                    ...styles.featureSettingTitle,
                    opacity:
                      cqEnabled && qiEnabled
                        ? 1
                        : 0.6
                  }}
                >
                  Groups per stream
                </label>

                <input
                  style={{
                    ...styles.compactInput,
                    opacity:
                      cqEnabled && qiEnabled
                        ? 1
                        : 0.65
                  }}
                  type="number"
                  min="1"
                  value={qiGroupsPerStream}
                  disabled={
                    !cqEnabled ||
                    !qiEnabled
                  }
                  onChange={e =>
                    handleQiGroupsChange(
                      e.target.value
                    )
                  }
                  onBlur={handleQiGroupsBlur}
                  placeholder="e.g. 4"
                />
              </div>
            </div>

            <div style={styles.featureRow}>
              <div style={styles.featurePrimary}>
                <label
                  style={styles.featurePrimaryLabel}
                  title={
                    cqEnabled
                      ? 'Voting is automatically enabled with Critical Questions.'
                      : ''
                  }
                >
                  <input
                    type="checkbox"
                    checked={votingEnabled}
                    disabled={cqEnabled}
                    onChange={e =>
                      handleVotingEnabledChange(
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Voting
                  </span>
                </label>
              </div>

              <div style={styles.featureSettingPanel}>
                <div style={styles.featureSettingTitle}>
                  {cqEnabled
                    ? 'Automatically enabled with Critical Questions'
                    : 'Allow live class voting without Critical Questions'}
                </div>
              </div>

              <div style={styles.featureEmptyCell} />
            </div>

            <div style={styles.featureRow}>
              <div style={styles.featurePrimary}>
                <label style={styles.featurePrimaryLabel}>
                  <input
                    type="checkbox"
                    checked={attendanceEnabled}
                    onChange={e =>
                      handleAttendanceEnabledChange(
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Attendance
                  </span>
                </label>
              </div>

              <div style={styles.featureEmptyCell} />
              <div style={styles.featureEmptyCell} />
            </div>

            <div style={styles.featureRow}>
              <div style={styles.featurePrimary}>
                <label style={styles.featurePrimaryLabel}>
                  <input
                    type="checkbox"
                    checked={
                      courseProgressEnabled
                    }
                    onChange={e =>
                      handleFeatureToggle(
                        'course_progress_enabled',
                        setCourseProgressEnabled,
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Course Progress
                  </span>
                </label>
              </div>

              <div style={styles.featureSettingPanel}>
                <div style={styles.featureSettingTitle}>
                  Show Course Progress to students and instructors
                </div>
              </div>

              <div style={styles.featureEmptyCell} />
            </div>


            <div style={styles.featureRow}>
              <div style={styles.featurePrimary}>
                <label style={styles.featurePrimaryLabel}>
                  <input
                    type="checkbox"
                    checked={chatEnabled}
                    onChange={e =>
                      handleFeatureToggle(
                        'chat_enabled',
                        setChatEnabled,
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Chat
                  </span>
                </label>
              </div>

              <div style={styles.featureSettingPanel}>
                <div style={styles.featureSettingTitle}>
                  Allow course and group chat for this course
                </div>
              </div>

              <div style={styles.featureEmptyCell} />
            </div>

            <div
              style={{
                ...styles.featureRow,
                opacity:
                  gradingMode === 'traditional'
                    ? 0.48
                    : 1
              }}
            >
              <div style={styles.featurePrimary}>
                <label
                  style={{
                    ...styles.featurePrimaryLabel,
                    cursor:
                      gradingMode === 'traditional'
                        ? 'default'
                        : 'pointer'
                  }}
                  title={
                    gradingMode === 'traditional'
                      ? 'Disabled by traditional grading'
                      : ''
                  }
                >
                  <input
                    type="checkbox"
                    checked={selfEvalEnabled}
                    disabled={
                      gradingMode === 'traditional'
                    }
                    onChange={e =>
                      handleFeatureToggle(
                        'self_eval_enabled',
                        setSelfEvalEnabled,
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Self-Evaluation
                  </span>
                </label>
              </div>

              <div
                style={{
                  ...styles.featureSettingPanel,
                  opacity:
                    selfEvalEnabled &&
                    gradingMode !== 'traditional'
                      ? 1
                      : 0.42
                }}
              >
                <div style={styles.featurePanelHeading}>
                  Mid-Term
                </div>

                <div style={styles.datePair}>
                  <div style={styles.dateField}>
                    <label style={styles.featureSettingTitle}>
                      Open
                    </label>

                                        <CourseDatePicker
                      value={
                        midEvalOpen
                      }
                      disabled={
                        !selfEvalEnabled ||
                        gradingMode ===
                          'traditional'
                      }
                      onChange={
                        value =>
                          handleDateChange(
                            setMidEvalOpen,
                            'mid_eval_open_date',
                            value
                          )
                      }
                      course={
                        datePickerCourse
                      }
                      {...datePickerContext}
                    />
                  </div>

                  <div style={styles.dateField}>
                    <label style={styles.featureSettingTitle}>
                      Close
                    </label>

                                        <CourseDatePicker
                      value={
                        midEvalClose
                      }
                      initialMonthDate={
                        midEvalOpen ||
                        ''
                      }
                      disabled={
                        !selfEvalEnabled ||
                        gradingMode ===
                          'traditional'
                      }
                      onChange={
                        value =>
                          handleDateChange(
                            setMidEvalClose,
                            'mid_eval_close_date',
                            value
                          )
                      }
                      course={
                        datePickerCourse
                      }
                      {...datePickerContext}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  ...styles.featureSettingPanel,
                  opacity:
                    selfEvalEnabled &&
                    gradingMode !== 'traditional'
                      ? 1
                      : 0.42
                }}
              >
                <div style={styles.featurePanelHeading}>
                  Final
                </div>

                <div style={styles.datePair}>
                  <div style={styles.dateField}>
                    <label style={styles.featureSettingTitle}>
                      Open
                    </label>

                                        <CourseDatePicker
                      value={
                        finalEvalOpen
                      }
                      disabled={
                        !selfEvalEnabled ||
                        gradingMode ===
                          'traditional'
                      }
                      onChange={
                        value =>
                          handleDateChange(
                            setFinalEvalOpen,
                            'final_eval_open_date',
                            value
                          )
                      }
                      course={
                        datePickerCourse
                      }
                      {...datePickerContext}
                    />
                  </div>

                  <div style={styles.dateField}>
                    <label style={styles.featureSettingTitle}>
                      Close
                    </label>

                                        <CourseDatePicker
                      value={
                        finalEvalClose
                      }
                      initialMonthDate={
                        finalEvalOpen ||
                        ''
                      }
                      disabled={
                        !selfEvalEnabled ||
                        gradingMode ===
                          'traditional'
                      }
                      onChange={
                        value =>
                          handleDateChange(
                            setFinalEvalClose,
                            'final_eval_close_date',
                            value
                          )
                      }
                      course={
                        datePickerCourse
                      }
                      {...datePickerContext}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.featureRow}>
              <div style={styles.featurePrimary}>
                <label style={styles.featurePrimaryLabel}>
                  <input
                    type="checkbox"
                    checked={streamsEnabled}
                    onChange={e =>
                      handleFeatureToggle(
                        'streams_enabled',
                        setStreamsEnabled,
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Streams
                  </span>
                </label>
              </div>

              <div style={styles.featureEmptyCell} />
              <div style={styles.featureEmptyCell} />
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionHeading}>
            Class Times & Rooms
          </h3>

          <p style={styles.sectionHelp}>
            Set the normal time and room for each recurring class day.
            Individual class dates can be overridden in Attendance when
            Variable Timings or Variable Rooms are enabled.
          </p>

          {classDays
            .filter(Boolean)
            .sort(
              (a, b) =>
                DAYS.indexOf(a) -
                DAYS.indexOf(b)
            )
            .map(day => (
              <div
                key={day}
                style={{
                  ...styles.row,
                  alignItems: 'flex-end',
                  gap: '14px',
                  marginBottom: '12px',
                  flexWrap: 'wrap'
                }}
              >
                <div
                  style={{
                    minWidth: '100px',
                    paddingBottom: '10px',
                    fontWeight: '700'
                  }}
                >
                  {day}
                </div>

                <div
                  style={{
                    ...styles.field,
                    maxWidth: '200px'
                  }}
                >
                  <label style={styles.label}>
                    Time
                  </label>

                  <input
                    style={styles.input}
                    type="time"
                    value={
                      classTimes[day] || ''
                    }
                    onChange={event =>
                      handleClassTimeChange(
                        day,
                        event.target.value
                      )
                    }
                  />
                </div>

                <div
                  style={{
                    ...styles.field,
                    maxWidth: '260px'
                  }}
                >
                  <label style={styles.label}>
                    Room
                  </label>

                  <input
                    style={styles.input}
                    type="text"
                    value={
                      classrooms[day] ||
                      ''
                    }
                    placeholder="e.g. A108"
                    onChange={event => {
                      const next = {
                        ...classrooms,
                        [day]:
                          event.target.value
                      };

                      setClassrooms(
                        next
                      );

                      scheduleSave(
                        'classrooms',
                        {
                          classrooms:
                            next
                        }
                      );
                    }}
                    onBlur={() =>
                      flushScheduledSave(
                        'classrooms',
                        {
                          classrooms
                        }
                      )
                    }
                  />
                </div>
              </div>
            ))}
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionHeading}>
            Grading Mode
          </h3>

          <div
            style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}
          >
            {['ungrading', 'traditional']
              .map(mode => (
                <label
                  key={mode}
                  style={{
                    ...styles.checkLabel,
                    gap: '8px'
                  }}
                >
                  <input
                    type="radio"
                    name="gradingMode"
                    value={mode}
                    checked={
                      gradingMode === mode
                    }
                    onChange={() =>
                      handleGradingModeChange(
                        mode
                      )
                    }
                  />

                  {mode === 'ungrading'
                    ? 'Ungrading (current)'
                    : 'Traditional grading'}
                </label>
              ))}
          </div>

          {gradingMode === 'traditional' && (
            <>
              <p
                style={{
                  ...styles.muted,
                  marginTop: '10px',
                  marginBottom: '12px'
                }}
              >
                Traditional grading
                automatically disables
                Self-Evaluation. Assignment
                weights are configured in the
                Assignments tab.
              </p>

              <div
                style={{
                  padding:
                    '12px 14px',
                  border:
                    '1px solid #dde4ea',
                  borderRadius:
                    '9px',
                  background:
                    '#fbfcfd',
                  maxWidth:
                    '560px'
                }}
              >
                <div
                  style={{
                    fontSize:
                      '13px',
                    fontWeight:
                      '800',
                    color:
                      '#1f2933',
                    marginBottom:
                      '8px'
                  }}
                >
                  Multiple Instructor Grading
                </div>

                <label
                  style={{
                    ...styles.checkLabel,
                    marginBottom:
                      '7px'
                  }}
                >
                  <input
                    type="radio"
                    name="instructorGradingMethod"
                    checked={
                      instructorGradingMethod ===
                      'single'
                    }
                    onChange={() =>
                      handleInstructorGradingMethodChange(
                        'single'
                      )
                    }
                  />

                  <span>
                    Single grade — the first instructor grade becomes the official grade
                  </span>
                </label>

                <label
                  style={
                    styles.checkLabel
                  }
                >
                  <input
                    type="radio"
                    name="instructorGradingMethod"
                    checked={
                      instructorGradingMethod ===
                      'average'
                    }
                    onChange={() =>
                      handleInstructorGradingMethodChange(
                        'average'
                      )
                    }
                  />

                  <span>
                    Average instructor grades
                  </span>
                </label>
              </div>
            </>
          )}
        </div>

        {session?.isSuperadmin && (
          <div style={styles.section}>
            <h3 style={styles.sectionHeading}>
              Honors Course
            </h3>

            <p style={styles.sectionHelp}>
              Honors students can access this
              course through the Honors Course
              Readings library.
            </p>

            <label style={styles.checkLabel}>
              <input
                type="checkbox"
                checked={isHonors}
                onChange={event => {
                  const checked =
                    event.target.checked;

                  setIsHonors(checked);

                  saveChanges({
                    is_honors: checked
                  });
                }}
              />

              <span>
                Designate as an Honors course
              </span>
            </label>
          </div>
        )}

        <div style={styles.section}>
          <h3 style={styles.sectionHeading}>
            Course Visibility
          </h3>

          <div style={styles.liveRow}>
            <div>
              <div style={styles.liveStatus}>
                Status:{' '}
                <strong
                  style={{
                    color: isLive
                      ? '#216e45'
                      : '#a12a2a'
                  }}
                >
                  {isLive
                    ? 'Live — visible to students'
                    : 'Draft — hidden from students'}
                </strong>
              </div>

              <div style={styles.liveSub}>
                {isLive
                  ? 'Students can currently access this course.'
                  : 'Students cannot see this course until you make it live.'}
              </div>
            </div>

            <button
              type="button"
              style={
                isLive
                  ? styles.draftBtn
                  : styles.liveBtn
              }
              onClick={handleToggleLive}
              disabled={visibilitySaving}
            >
              {visibilitySaving
                ? 'Saving...'
                : isLive
                  ? 'Make Draft'
                  : 'Make Live'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  headingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  },

  subheading: {
    margin: 0,
    fontSize: '18px',
    color: '#1f2933'
  },

  muted: {
    color: '#66788a',
    fontSize: '14px',
    margin: '0 0 24px'
  },

  saveStatus: {
    fontSize: '12px',
    color: '#66788a',
    fontWeight: '600'
  },

  saveStatusSaved: {
    color: '#216e45'
  },

  error: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  row: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '24px'
  },

  field: {
    flex: 1,
    minWidth: '160px'
  },

  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '6px',
    color: '#1f2933'
  },

  input: {
    width: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '14px',
    boxSizing: 'border-box',
    background: 'white'
  },

  section: {
    marginBottom: '24px'
  },

  sectionHeading: {
    fontSize: '15px',
    color: '#1f2933',
    margin: '0 0 12px'
  },

  instructorTopRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(180px, 0.78fr) minmax(220px, 1fr) 38px',
    gap: '10px',
    alignItems: 'stretch',
    maxWidth: '720px',
    overflowX: 'auto'
  },

  additionalInstructorRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '10px',
    maxWidth: '672px'
  },

  additionalInstructorRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(220px, 1fr) minmax(220px, 1fr)',
    gap: '10px'
  },

  addInstructorButton: {
    width: '38px',
    height: '38px',
    alignSelf: 'center',
    border: '1px solid #b8c6d1',
    borderRadius: '8px',
    background: 'white',
    color: '#215b7d',
    fontSize: '23px',
    fontWeight: '500',
    lineHeight: 1,
    cursor: 'pointer'
  },

  addInstructorButtonDisabled: {
    opacity: 0.4,
    cursor: 'default'
  },

  instructorSettingCard: {
    padding: '12px 14px',
    border: '1px solid #dde4ea',
    borderRadius: '9px',
    background: '#fbfcfd'
  },

  primaryInstructorName: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1f2933'
  },

  primaryInstructorEmail: {
    marginTop: '3px',
    fontSize: '12px',
    color: '#66788a'
  },

  instructorSettingNote: {
    marginTop: '6px',
    fontSize: '11px',
    color: '#66788a'
  },

  featureRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },

  featureRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(190px, 0.78fr) minmax(245px, 1.11fr) minmax(245px, 1.11fr)',
    gap: '12px',
    alignItems: 'stretch',
    padding: '12px',
    border: '1px solid #dde4ea',
    borderRadius: '10px',
    background: '#fbfcfd',
    overflowX: 'auto'
  },

  featurePrimary: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '8px 6px'
  },

  featurePrimaryLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    fontSize: '14px',
    fontWeight: '700',
    color: '#1f2933',
    cursor: 'pointer',
    lineHeight: '1.35'
  },

  featureSettingPanel: {
    minWidth: 0,
    padding: '10px 12px',
    border: '1px solid #e3e8ed',
    borderRadius: '8px',
    background: 'white',
    transition: 'opacity 0.15s ease'
  },

  featureEmptyCell: {
    minHeight: '1px'
  },

  featureSecondaryToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#1f2933',
    marginBottom: '12px',
    lineHeight: '1.35'
  },

  featurePanelHeading: {
    fontSize: '12px',
    fontWeight: '800',
    color: '#42515f',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '10px'
  },

  featureSettingTitle: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '700',
    color: '#66788a',
    marginBottom: '5px'
  },

  compactInput: {
    width: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '7px',
    padding: '7px 9px',
    fontSize: '13px',
    boxSizing: 'border-box',
    background: 'white'
  },

  datePair: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px'
  },

  dateField: {
    minWidth: 0
  },

  checkLabel: {
    fontSize: '14px',
    color: '#1f2933',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },

  liveRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  },

  liveStatus: {
    fontSize: '14px',
    color: '#1f2933',
    marginBottom: '4px'
  },

  liveSub: {
    fontSize: '13px',
    color: '#66788a'
  },

  liveBtn: {
    background: '#216e45',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0
  },

  draftBtn: {
    background: 'transparent',
    color: '#a12a2a',
    border: '1px solid #e2b8b8',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0
  },

  sectionHelp: {
    color: '#66788a',
    fontSize: '13px',
    lineHeight: '1.5',
    margin: '-4px 0 12px'
  },

  courseImageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  },

  courseImagePreview: {
    width: '220px',
    aspectRatio: '16 / 9',
    borderRadius: '10px',
    overflow: 'hidden',
    background: '#f4f6f8',
    border: '1px solid #d7dee5',
    flexShrink: 0
  },

  courseImage: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },

  courseImagePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8996a3',
    fontSize: '13px',
    fontWeight: '600'
  },

  courseImageActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },

  hiddenFileInput: {
    display: 'none'
  },

  imageUploadBtn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 14px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  imageRemoveBtn: {
    background: 'white',
    color: '#a12a2a',
    border: '1px solid #e2b8b8',
    borderRadius: '8px',
    padding: '8px 14px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  imageStatus: {
    color: '#66788a',
    fontSize: '13px',
    fontWeight: '600'
  }
};